import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, ApiError, type CompetitionDetail, type CompetitionRules } from "../../lib/api";
import { analytics, AnalyticsEvent } from "../../lib/analytics";
import { trackApiError } from "../../lib/analytics/errorTracking";
import {
  Avatar,
  Badge,
  Button,
  ErrorNote,
  Icon,
  InfoNote,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  useCopy,
  useToast,
  type IconName,
} from "../../components/ui";

type Member = CompetitionDetail["members"][number];
type MemberAction =
  | { kind: "remove" | "revive" | "promote" | "demote" | "transfer"; member: Member }
  | null;

/** Whether the viewer has owner-level authority here — either by actually owning the
 *  competition, or as a platform admin overseeing it without being a member. */
function hasOwnerAuthority(competition: CompetitionDetail): boolean {
  return competition.isOwner || competition.isPlatformAdmin;
}

export function SettingsPanel({ competition }: { competition: CompetitionDetail }) {
  return (
    <div className="space-y-5">
      <SettingsIntro competition={competition} />
      <GeneralSettings competition={competition} />
      <EntrySettings competition={competition} />
      <MemberSettings competition={competition} />
      <RulesSettings competition={competition} />
      <DangerZone competition={competition} />
    </div>
  );
}

function SettingsIntro({ competition }: { competition: CompetitionDetail }) {
  return (
    <InfoNote icon="shield">
      {competition.isPlatformAdmin
        ? "You're managing this as a platform admin — you have full owner-level control, and every action here is recorded."
        : competition.isOwner
          ? "You own this competition. Changes to members and access are recorded for accountability."
          : "You are a group admin. You can manage settings and players; ownership, admin roles and deletion stay with the owner."}
    </InfoNote>
  );
}

function SettingsSection({
  icon,
  title,
  description,
  children,
}: {
  icon: IconName;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="card p-5 sm:p-6">
      <div className="mb-5 flex items-start gap-3.5 border-b border-line pb-5">
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent-text">
          <Icon name={icon} className="size-5" />
        </span>
        <div>
          <h2 className="display-title text-2xl text-fg">{title}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-fg-subtle">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

/* General ------------------------------------------------------------------ */

function GeneralSettings({ competition }: { competition: CompetitionDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(competition.name);
  const [description, setDescription] = useState(competition.description ?? "");

  useEffect(() => {
    setName(competition.name);
    setDescription(competition.description ?? "");
  }, [competition.name, competition.description]);

  const mutation = useMutation({
    mutationFn: () =>
      api.competitions.update(competition.id, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: () => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: "details",
      });
      toast.show("Competition details saved");
      void queryClient.invalidateQueries({ queryKey: ["competition", competition.id] });
      void queryClient.invalidateQueries({ queryKey: ["competitions"] });
    },
    onError: (error) => trackApiError(error, "update_competition_details"),
  });

  const changed =
    name.trim() !== competition.name ||
    description.trim() !== (competition.description ?? "");

  return (
    <SettingsSection
      icon="settings"
      title="Competition details"
      description="The name and context everyone sees. The league and season stay fixed once created."
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          minLength={2}
          maxLength={80}
          required
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={280}
          rows={3}
          placeholder="What is this competition for?"
          hint={`${description.length}/280`}
        />
        {mutation.error && (
          <MutationError error={mutation.error} fallback="Couldn't update the competition." />
        )}
        <div className="flex justify-end">
          <Button
            type="submit"
            loading={mutation.isPending}
            disabled={!changed || name.trim().length < 2}
          >
            Save details
          </Button>
        </div>
      </form>
    </SettingsSection>
  );
}

/* Entry access ------------------------------------------------------------- */

function EntrySettings({ competition }: { competition: CompetitionDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { copied, copy } = useCopy();
  const [rotateOpen, setRotateOpen] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["competition", competition.id] });
    void queryClient.invalidateQueries({ queryKey: ["competitions"] });
  };

  const toggle = useMutation({
    mutationFn: () =>
      api.competitions.update(competition.id, { isJoinable: !competition.isJoinable }),
    onSuccess: () => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: "is_joinable",
      });
      toast.show(competition.isJoinable ? "New entries closed" : "New entries reopened");
      invalidate();
    },
    onError: (error) => trackApiError(error, "toggle_entry_access"),
  });

  const rotate = useMutation({
    mutationFn: () => api.competitions.update(competition.id, { regenerateInviteCode: true }),
    onSuccess: () => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: "invite_code",
      });
      setRotateOpen(false);
      toast.show("New invite code created");
      invalidate();
    },
    onError: (error) => trackApiError(error, "rotate_invite_code"),
  });

  return (
    <SettingsSection
      icon="ticket"
      title="Entry access"
      description="Control whether the invite code accepts new players. Existing players are never affected."
    >
      <div className="surface-soft flex items-center justify-between gap-4 p-4">
        <div className="min-w-0">
          <p className="font-semibold text-fg">Accept new players</p>
          <p className="mt-1 text-[13px] leading-relaxed text-fg-subtle">
            {competition.isJoinable
              ? "Anyone with the code can join until round 1 locks."
              : "The code is paused until you reopen entry."}
          </p>
        </div>
        <Switch
          checked={competition.isJoinable}
          onChange={() => toggle.mutate()}
          label="Accept new players"
          disabled={toggle.isPending || competition.status === "complete"}
        />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <p className="mb-1.5 text-sm font-medium text-fg-muted">Invite code</p>
          <div className="flex h-12 items-center rounded-2xl border border-line bg-surface-sunken px-4">
            <code className="tabular flex-1 truncate text-lg font-bold tracking-[0.22em] text-accent-text">
              {competition.inviteCode ?? "Unavailable"}
            </code>
            <button
              type="button"
              disabled={!competition.inviteCode}
              aria-label={copied ? "Invite code copied" : "Copy invite code"}
              onClick={async () => {
                if (await copy(competition.inviteCode ?? "")) toast.show("Invite code copied");
              }}
              className="-mr-1.5 grid size-10 place-items-center rounded-xl text-fg-subtle transition-colors hover:bg-tint hover:text-fg"
            >
              <Icon name={copied ? "check" : "copy"} className="size-[18px]" />
            </button>
          </div>
        </div>
        <Button variant="secondary" onClick={() => setRotateOpen(true)}>
          <Icon name="refresh" className="size-4" /> Rotate
        </Button>
      </div>

      {(toggle.error || rotate.error) && (
        <div className="mt-4">
          <MutationError
            error={toggle.error ?? rotate.error}
            fallback="Couldn't update entry access."
          />
        </div>
      )}

      <Modal
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        title="Rotate the invite code"
        description="The current code stops working immediately. Existing members stay in the competition."
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setRotateOpen(false)}>
              Cancel
            </Button>
            <Button className="flex-1" onClick={() => rotate.mutate()} loading={rotate.isPending}>
              Create new code
            </Button>
          </div>
        }
      >
        <InfoNote icon="alert">
          Anyone you've already shared the old code with won't be able to join with it.
        </InfoNote>
      </Modal>
    </SettingsSection>
  );
}

/* Members ------------------------------------------------------------------ */

function MemberSettings({ competition }: { competition: CompetitionDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [action, setAction] = useState<MemberAction>(null);
  const [reason, setReason] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [claimLinkFor, setClaimLinkFor] = useState<Member | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!action) return;
      if (action.kind === "remove")
        return api.competitions.removeMember(competition.id, action.member.userId, reason.trim());
      if (action.kind === "revive")
        return api.competitions.revive(competition.id, action.member.entryId, reason.trim());
      if (action.kind === "transfer")
        return api.competitions.transferOwnership(competition.id, action.member.userId);
      return api.competitions.updateMemberRole(
        competition.id,
        action.member.userId,
        action.kind === "promote" ? "admin" : "player",
      );
    },
    onSuccess: () => {
      if (!action) return;
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: `member_${action.kind}`,
      });
      toast.show(
        {
          remove: `${action.member.displayName} was removed`,
          revive: `${action.member.displayName} is back in`,
          promote: `${action.member.displayName} is now an admin`,
          demote: `${action.member.displayName} is now a player`,
          transfer: `${action.member.displayName} now owns this competition`,
        }[action.kind],
      );
      setAction(null);
      setReason("");
      void queryClient.invalidateQueries({ queryKey: ["competition", competition.id] });
      void queryClient.invalidateQueries({ queryKey: ["leaderboard", competition.id] });
      void queryClient.invalidateQueries({ queryKey: ["results-grid", competition.id] });
      void queryClient.invalidateQueries({ queryKey: ["competitions"] });
    },
    onError: (error) => trackApiError(error, `member_${action?.kind ?? "action"}`),
  });

  function openAction(next: NonNullable<MemberAction>) {
    setReason("");
    mutation.reset();
    setAction(next);
  }

  const roundLocked =
    competition.currentRound != null &&
    ["locked", "settled"].includes(competition.currentRound.status) &&
    competition.status === "running";

  return (
    <SettingsSection
      icon="users"
      title={`People · ${competition.members.length}`}
      description="Manage access and correct exceptional player outcomes. Every action is recorded."
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-md text-sm leading-relaxed text-fg-subtle">
          Add someone who isn't using the app yet — they play through you until they claim their
          own account.
        </p>
        <Button size="sm" variant="secondary" onClick={() => setAddingPlayer(true)}>
          <Icon name="plus" className="size-4" /> Add player
        </Button>
      </div>

      {roundLocked && (
        <div className="mb-4">
          <InfoNote icon="shield">
            Active players can't be removed or revived while the current round is locked. This
            protects the live result.
          </InfoNote>
        </div>
      )}

      <div className="divide-y divide-line overflow-hidden rounded-2xl border border-line">
        {competition.members.map((member) => {
          const ownerAuthority = hasOwnerAuthority(competition);
          const canRemove =
            !member.isOwner && !member.isSelf && (member.role === "player" || ownerAuthority);
          const roundAcceptsChanges =
            !competition.currentRound ||
            ["scheduled", "open"].includes(competition.currentRound.status);
          const canRevive =
            competition.status === "running" &&
            (!competition.currentRound || competition.currentRound.status === "open");
          const canRemoveNow =
            canRemove &&
            competition.status === "running" &&
            (member.status === "out" || roundAcceptsChanges);

          return (
            <div
              key={member.entryId}
              className="flex flex-col gap-3 bg-surface p-4 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Avatar
                  name={member.displayName}
                  color={member.avatarColor}
                  size={40}
                  dimmed={member.status === "out"}
                />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="truncate font-semibold text-fg">{member.displayName}</p>
                    {member.isSelf && <span className="text-[12px] text-fg-faint">You</span>}
                    {member.isManaged && <Badge tone="info">Unclaimed</Badge>}
                  </div>
                  <p className="mt-0.5 text-[12px] text-fg-faint">
                    Joined{" "}
                    {new Date(member.joinedAt).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {member.isOwner ? (
                  <Badge tone="warn">Owner</Badge>
                ) : member.role === "admin" ? (
                  <Badge tone="info">Admin</Badge>
                ) : null}
                <Badge tone={member.status === "alive" ? "alive" : "out"}>{member.status}</Badge>

                {member.isManaged && (member.role === "player" || ownerAuthority) && (
                  <Button size="sm" variant="secondary" onClick={() => setClaimLinkFor(member)}>
                    <Icon name="link" className="size-4" /> Claim link
                  </Button>
                )}
                {member.status === "out" &&
                  canRevive &&
                  !member.isSelf &&
                  (member.role === "player" || ownerAuthority) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => openAction({ kind: "revive", member })}
                    >
                      Revive
                    </Button>
                  )}
                {ownerAuthority && !member.isOwner && !member.isSelf && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      openAction({
                        kind: member.role === "admin" ? "demote" : "promote",
                        member,
                      })
                    }
                  >
                    {member.role === "admin" ? "Remove admin" : "Make admin"}
                  </Button>
                )}
                {ownerAuthority && !member.isOwner && !member.isSelf && !member.isManaged && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openAction({ kind: "transfer", member })}
                  >
                    Transfer
                  </Button>
                )}
                {canRemoveNow && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => openAction({ kind: "remove", member })}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <MemberActionModal
        action={action}
        reason={reason}
        setReason={setReason}
        busy={mutation.isPending}
        error={mutation.error}
        onClose={() => setAction(null)}
        onConfirm={() => mutation.mutate()}
      />

      <AddManagedPlayerModal
        open={addingPlayer}
        competitionId={competition.id}
        onClose={() => setAddingPlayer(false)}
      />

      {claimLinkFor && (
        <ClaimLinkModal
          competitionId={competition.id}
          member={claimLinkFor}
          onClose={() => setClaimLinkFor(null)}
        />
      )}
    </SettingsSection>
  );
}

function MemberActionModal({
  action,
  reason,
  setReason,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  action: MemberAction;
  reason: string;
  setReason: (value: string) => void;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!action) return null;

  const copy = {
    remove: {
      title: `Remove ${action.member.displayName}?`,
      body: "Their access will be revoked immediately. Existing picks and results stay in the audit history.",
      button: "Remove player",
      danger: true,
    },
    revive: {
      title: `Revive ${action.member.displayName}?`,
      body: "They will return as an active player. Used teams and previous picks stay unchanged.",
      button: "Revive player",
      danger: false,
    },
    promote: {
      title: `Make ${action.member.displayName} an admin?`,
      body: "They will be able to edit settings, manage ordinary players and settle rounds.",
      button: "Make admin",
      danger: false,
    },
    demote: {
      title: "Remove admin access?",
      body: `${action.member.displayName} will remain in the competition as a player.`,
      button: "Remove admin",
      danger: false,
    },
    transfer: {
      title: "Transfer ownership?",
      body: `${action.member.displayName} will gain permanent owner controls. You will remain a group admin.`,
      button: "Transfer ownership",
      danger: true,
    },
  }[action.kind];

  const needsReason = action.kind === "remove" || action.kind === "revive";

  return (
    <Modal
      open
      onClose={onClose}
      title={copy.title}
      description={copy.body}
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            variant={copy.danger ? "danger" : "primary"}
            onClick={onConfirm}
            loading={busy}
            disabled={needsReason && reason.trim().length < 3}
          >
            {copy.button}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {needsReason && (
          <Input
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            minLength={3}
            maxLength={280}
            placeholder={action.kind === "remove" ? "Left the group" : "Admin correction"}
            hint="Saved in the competition audit history."
            autoFocus
          />
        )}
        {Boolean(error) && (
          <MutationError error={error} fallback="That action couldn't be completed." />
        )}
      </div>
    </Modal>
  );
}

function AddManagedPlayerModal({
  open,
  competitionId,
  onClose,
}: {
  open: boolean;
  competitionId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [created, setCreated] = useState<{ claimToken: string; displayName: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.competitions.addManagedPlayer(competitionId, displayName.trim()),
    onSuccess: (result) => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId,
        field: "managed_player_added",
      });
      setCreated({ claimToken: result.claimToken, displayName: displayName.trim() });
      void queryClient.invalidateQueries({ queryKey: ["competition", competitionId] });
      void queryClient.invalidateQueries({ queryKey: ["leaderboard", competitionId] });
      void queryClient.invalidateQueries({ queryKey: ["results-grid", competitionId] });
    },
    onError: (error) => trackApiError(error, "add_managed_player"),
  });

  function close() {
    setDisplayName("");
    setCreated(null);
    mutation.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={created ? `${created.displayName} added` : "Add a player"}
      description={
        created
          ? "Share this link with them whenever they're ready to take over their own account."
          : "For someone who plays through you rather than the app — you can set their pick each round."
      }
      footer={
        created ? (
          <Button block onClick={close}>
            Done
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              loading={mutation.isPending}
              disabled={displayName.trim().length < 2}
              onClick={() => mutation.mutate()}
            >
              Add player
            </Button>
          </div>
        )
      }
    >
      {created ? (
        <div className="space-y-4">
          <ClaimLinkDisplay token={created.claimToken} />
          <InfoNote icon="shield">
            Until they claim it, set their picks from the Grid tab — tap their cell in the open
            round.
          </InfoNote>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Dave"
            minLength={2}
            maxLength={24}
            autoFocus
            icon="user"
            hint="Shown on the leaderboard exactly like everyone else's."
          />
          {mutation.error && (
            <MutationError error={mutation.error} fallback="Couldn't add that player." />
          )}
        </form>
      )}
    </Modal>
  );
}

function ClaimLinkModal({
  competitionId,
  member,
  onClose,
}: {
  competitionId: string;
  member: Member;
  onClose: () => void;
}) {
  const toast = useToast();
  const [token, setToken] = useState(member.claimToken ?? "");

  const rotate = useMutation({
    mutationFn: () => api.competitions.reissueClaimLink(competitionId, member.userId),
    onSuccess: (result) => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId,
        field: "claim_link_reissued",
      });
      setToken(result.claimToken);
      toast.show("New claim link created");
    },
    onError: (error) => trackApiError(error, "reissue_claim_link"),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`${member.displayName}'s claim link`}
      description={`Anyone with this link can sign in as ${member.displayName} and take over their picks and history. Only share it with them.`}
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => rotate.mutate()} loading={rotate.isPending}>
            <Icon name="refresh" className="size-4" /> New link
          </Button>
          <Button className="flex-1" onClick={onClose}>
            Done
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <ClaimLinkDisplay token={token} />
        {rotate.error && (
          <MutationError error={rotate.error} fallback="Couldn't create a new link." />
        )}
      </div>
    </Modal>
  );
}

function ClaimLinkDisplay({ token }: { token: string }) {
  const { copied, copy } = useCopy();
  const toast = useToast();
  const url = `${window.location.origin}/claim/${token}`;

  return (
    <div className="flex h-12 items-center gap-2 rounded-2xl border border-line bg-surface-sunken px-3.5">
      <Icon name="link" className="size-4 shrink-0 text-fg-faint" />
      <code className="flex-1 truncate text-[13px] text-accent-text">{url}</code>
      <button
        type="button"
        aria-label={copied ? "Link copied" : "Copy link"}
        onClick={async () => {
          if (await copy(url)) toast.show("Claim link copied");
        }}
        className="-mr-1 grid size-9 shrink-0 place-items-center rounded-xl text-fg-subtle transition-colors hover:bg-tint hover:text-fg"
      >
        <Icon name={copied ? "check" : "copy"} className="size-4" />
      </button>
    </div>
  );
}

/* Rules -------------------------------------------------------------------- */

function RulesSettings({ competition }: { competition: CompetitionDetail }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [rules, setRules] = useState(competition.rules);

  useEffect(() => setRules(competition.rules), [competition.rules]);

  const mutation = useMutation({
    mutationFn: () => api.competitions.updateRules(competition.id, rules),
    onSuccess: () => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: "rules",
      });
      toast.show("Rules saved");
      void queryClient.invalidateQueries({ queryKey: ["competition", competition.id] });
      void queryClient.invalidateQueries({ queryKey: ["competitions"] });
    },
    onError: (error) => trackApiError(error, "update_rules"),
  });

  const changed = JSON.stringify(rules) !== JSON.stringify(competition.rules);

  // Once locked, an ordinary competition admin can still change pick-reveal timing — it can't
  // rewrite a result that's already happened — but nothing that determines who's eliminated. A
  // platform admin can still override everything, same as before; every change stays audited.
  const lockedForThisAdmin = competition.rulesLocked && !competition.isPlatformAdmin;

  const set = <K extends keyof CompetitionRules>(section: K, patch: Partial<CompetitionRules[K]>) =>
    setRules((current) => ({ ...current, [section]: { ...(current[section] as object), ...patch } }));

  return (
    <SettingsSection
      icon="shield"
      title="Game rules"
      description={
        competition.rulesLocked
          ? "Rules locked when the first pick was made or round one started, so nobody can move the goalposts."
          : "Editable until the first pick is made or round one locks. Saving lives changes every active player's allowance."
      }
    >
      {competition.rulesLocked && (
        <div className="mb-5">
          <InfoNote icon="shield">
            {competition.isPlatformAdmin
              ? "You're overriding locked rules as a platform admin — every change here is recorded. Changing lives sets the number for rounds from here on; it won't hand back a life anyone has already lost."
              : "Only pick reveal timing can still be changed. Everything else needs a platform admin, since it could otherwise be tilted after seeing how a round is going."}
          </InfoNote>
        </div>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
        className="space-y-5"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Lives"
            value={rules.elimination.lives}
            disabled={lockedForThisAdmin}
            onChange={(e) => set("elimination", { lives: Number(e.target.value) })}
          >
            <option value={0}>Sudden death</option>
            <option value={1}>1 life</option>
            <option value={2}>2 lives</option>
            <option value={3}>3 lives</option>
          </Select>

          <Select
            label="A draw counts as"
            value={rules.elimination.drawCountsAs}
            disabled={lockedForThisAdmin}
            onChange={(e) =>
              set("elimination", {
                drawCountsAs: e.target.value as CompetitionRules["elimination"]["drawCountsAs"],
              })
            }
          >
            <option value="loss">A loss</option>
            <option value="win">A win</option>
            <option value="void">Void</option>
          </Select>

          <Select
            label="No pick by deadline"
            value={rules.elimination.missedPick}
            disabled={lockedForThisAdmin}
            onChange={(e) =>
              set("elimination", {
                missedPick: e.target.value as CompetitionRules["elimination"]["missedPick"],
              })
            }
          >
            <option value="eliminate">Eliminate</option>
            <option value="auto_assign">Auto-assign</option>
            <option value="carry_forward">Carry last pick</option>
          </Select>

          <Select
            label="Picks lock"
            value={rules.picks.deadlineOffsetMinutes}
            disabled={lockedForThisAdmin}
            onChange={(e) => set("picks", { deadlineOffsetMinutes: Number(e.target.value) })}
          >
            <option value={0}>At first kick-off</option>
            <option value={60}>1 hour before</option>
            <option value={720}>12 hours before</option>
            <option value={1440}>24 hours before</option>
          </Select>

          <Select
            label="Reveal picks"
            value={rules.picks.reveal}
            onChange={(e) =>
              set("picks", { reveal: e.target.value as CompetitionRules["picks"]["reveal"] })
            }
            hint={competition.rulesLocked ? "Still editable" : undefined}
          >
            <option value="after_deadline">After deadline</option>
            <option value="immediately">Immediately</option>
            <option value="after_settlement">After settlement</option>
          </Select>

          <Select
            label="Team reuse"
            value={rules.picks.teamReuse}
            disabled={lockedForThisAdmin}
            onChange={(e) =>
              set("picks", { teamReuse: e.target.value as CompetitionRules["picks"]["teamReuse"] })
            }
          >
            <option value="never">Never</option>
            <option value="reset_on_exhaustion">Reset when exhausted</option>
            <option value="unlimited">Unlimited</option>
          </Select>

          <Select
            label="Team plays twice"
            value={rules.picks.teamPlaysTwice}
            disabled={lockedForThisAdmin}
            onChange={(e) =>
              set("picks", {
                teamPlaysTwice: e.target.value as CompetitionRules["picks"]["teamPlaysTwice"],
              })
            }
          >
            <option value="first_fixture">First fixture counts</option>
            <option value="must_win_both">Must win both</option>
          </Select>

          <Select
            label="Postponed fixture"
            value={rules.postponedFixture}
            disabled={lockedForThisAdmin}
            onChange={(e) =>
              setRules((current) => ({
                ...current,
                postponedFixture: e.target.value as CompetitionRules["postponedFixture"],
              }))
            }
          >
            <option value="void_survive">Survive; team becomes available</option>
            <option value="loss">Counts as a loss</option>
            <option value="hold_round">Hold the round</option>
          </Select>

          <div className="sm:col-span-2">
            <Select
              label="Total wipeout"
              value={rules.wipeout}
              disabled={lockedForThisAdmin}
              onChange={(e) =>
                setRules((current) => ({
                  ...current,
                  wipeout: e.target.value as CompetitionRules["wipeout"],
                }))
              }
            >
              <option value="full_reset">Full reset</option>
              <option value="revive_round">Revive, keep used teams</option>
              <option value="split">Split and finish</option>
              <option value="no_winner">Finish with no winner</option>
            </Select>
          </div>
        </div>

        {!competition.rulesLocked && (
          <p className="text-[13px] leading-relaxed text-fg-subtle">
            New players can join up until round 1 locks — this closes automatically and isn't
            configurable.
          </p>
        )}

        {mutation.error && (
          <MutationError error={mutation.error} fallback="Couldn't save the rules." />
        )}

        <div className="flex justify-end">
          <Button type="submit" loading={mutation.isPending} disabled={!changed}>
            Save rules
          </Button>
        </div>
      </form>
    </SettingsSection>
  );
}

/* Danger ------------------------------------------------------------------- */

function DangerZone({ competition }: { competition: CompetitionDetail }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.competitions.delete(competition.id, confirmation),
    onSuccess: async () => {
      analytics.track(AnalyticsEvent.CompetitionSettingsUpdated, {
        competitionId: competition.id,
        field: "deleted",
      });
      await queryClient.invalidateQueries({ queryKey: ["competitions"] });
      queryClient.removeQueries({ queryKey: ["competition", competition.id] });
      navigate("/", { replace: true });
    },
    onError: (error) => trackApiError(error, "delete_competition"),
  });

  return (
    <section className="rounded-card border border-danger-line bg-danger-soft p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-danger-text">
            <Icon name="trash" className="size-5" />
            <h2 className="font-semibold">Delete competition</h2>
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-fg-subtle">
            Permanently removes every round, pick and membership. The platform audit record is
            retained.
          </p>
        </div>
        {hasOwnerAuthority(competition) ? (
          <Button variant="danger" onClick={() => setOpen(true)}>
            Delete competition
          </Button>
        ) : (
          <Badge tone="neutral">Owner only</Badge>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Permanently delete competition"
        description="This cannot be undone. Every player, pick and round in this competition will be deleted."
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              variant="danger"
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={confirmation !== competition.name}
            >
              Delete forever
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label={`Type “${competition.name}” to confirm`}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
            autoFocus
          />
          {mutation.error && (
            <MutationError error={mutation.error} fallback="Couldn't delete the competition." />
          )}
        </div>
      </Modal>
    </section>
  );
}

function MutationError({ error, fallback }: { error: unknown; fallback: string }) {
  return <ErrorNote>{error instanceof ApiError ? error.message : fallback}</ErrorNote>;
}
